import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, Check, LinkIcon, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddDepositModal = ({ clientId, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [form, setForm] = useState({
    amount: '', deposit_date: new Date(), notes: ''
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activePlans, setActivePlans] = useState([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [clientBalance, setClientBalance] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [planRes, balRes] = await Promise.all([
          axios.get(`${API}/work-plans?client_id=${clientId}`),
          axios.get(`${API}/clients/${clientId}/balance`),
        ]);
        const plans = planRes.data?.plans || [];
        const balance = balRes.data?.balance || 0;
        setClientBalance(balance);
        const active = plans.filter(p => {
          const steps = p.steps || [];
          const hasActive = steps.some(s => s.status !== 'completed' && s.status !== 'cancelled');
          return hasActive && p.payment_status !== 'paid';
        });
        setActivePlans(active);
      } catch (e) { console.error(e); }
      setLoadingPlans(false);
    };
    fetchData();
  }, [clientId]);

  const togglePlan = (planId) => {
    setSelectedPlanIds(prev =>
      prev.includes(planId) ? prev.filter(id => id !== planId) : [...prev, planId]
    );
  };

  // Calculate the actual amount due (what the client owes)
  const totalOnAccountCost = activePlans
    .reduce((sum, p) => sum + (p.steps || [])
      .filter(s => s.status !== 'cancelled')
      .reduce((s, st) => s + (st.price || 0), 0), 0);
  const amountDue = clientBalance < 0 ? Math.abs(clientBalance) : Math.max(totalOnAccountCost - clientBalance, 0);

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error(isRTL ? 'أدخل المبلغ' : 'Enter amount');
      return;
    }
    try {
      await axios.post(`${API}/deposits`, {
        client_id: clientId,
        amount: parseFloat(form.amount),
        deposit_date: format(form.deposit_date, 'yyyy-MM-dd'),
        notes: form.notes,
        linked_plan_ids: selectedPlanIds
      });
      toast.success(t('success'));
      onSuccess();
      onClose();
    } catch { toast.error(t('error')); }
  };

  return (
    <div className="p-4 border-2 border-dashed border-slate-200 rounded-xl space-y-4 bg-slate-50" data-testid="add-deposit-modal">
      <div className="grid grid-cols-2 gap-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <Label className="text-slate-700">{t('amount')}</Label>
          <input type="number" value={form.amount}
            onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mt-1"
            style={{ direction: 'ltr' }}
            data-testid="deposit-amount-input" />
        </div>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <Label className="text-slate-700">{t('depositDate')}</Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full mt-1 h-10 justify-start font-normal" dir="ltr" data-testid="deposit-date-btn">
                <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                {format(form.deposit_date, 'dd/MM/yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={form.deposit_date}
                onSelect={(date) => { if (date) { setForm(prev => ({ ...prev, deposit_date: date })); setCalendarOpen(false); } }}
                initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className={isRTL ? 'text-right' : 'text-left'}>
        <Label className="text-slate-700">{t('notes')}</Label>
        <input type="text" value={form.notes}
          onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mt-1"
          placeholder={isRTL ? 'أدخل الملاحظات...' : 'Enter notes...'}
          data-testid="deposit-notes-input" />
      </div>

      {/* Link to Work Plans */}
      <div className={isRTL ? 'text-right' : 'text-left'}>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-slate-700 flex items-center gap-1.5">
            <LinkIcon className="w-3.5 h-3.5" />
            {isRTL ? 'ربط الإيداع بخطط العمل' : 'Link deposit to work plans'}
          </Label>
          {amountDue > 0 && (
            <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-lg border border-red-200" dir="ltr" data-testid="amount-due-badge">
              {isRTL ? 'المبلغ المستحق:' : 'Amount Due:'} {amountDue.toLocaleString()} {isRTL ? 'درهم' : 'AED'}
            </span>
          )}
        </div>
        {loadingPlans ? (
          <div className="text-xs text-slate-400 py-2">{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>
        ) : activePlans.length === 0 ? (
          <div className="text-xs text-slate-400 py-2 bg-white rounded-lg px-3 border border-slate-100">
            {isRTL ? 'لا يوجد خطط عمل نشطة غير مدفوعة' : 'No active unpaid work plans'}
          </div>
        ) : (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {activePlans.map(plan => {
              const isSelected = selectedPlanIds.includes(plan.id);
              const steps = (plan.steps || []).filter(s => s.status !== 'cancelled');
              const total = steps.reduce((s, st) => s + (st.price || 0), 0);
              const isCompany = plan.plan_type === 'company';
              const title = isCompany ? plan.company_name : plan.employee_name;
              const payBadge = plan.payment_status === 'on_account'
                ? { label: isRTL ? 'على الحساب' : 'On Account', cls: 'bg-amber-100 text-amber-700' }
                : { label: isRTL ? 'غير مدفوع' : 'Unpaid', cls: 'bg-red-100 text-red-600' };
              return (
                <div key={plan.id}
                  onClick={() => togglePlan(plan.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                    isSelected
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid={`plan-link-${plan.id}`}>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                    isSelected ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-slate-300'
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="shrink-0">
                    {isCompany
                      ? <Building2 className="w-4 h-4 text-slate-400" />
                      : <User className="w-4 h-4 text-slate-400" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{title}</p>
                    <p className="text-[10px] text-slate-400">{steps.length} {isRTL ? 'خطوات' : 'steps'} <span className="text-slate-300">|</span> <span dir="ltr">{total.toLocaleString()}</span> {isRTL ? 'درهم' : 'AED'}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${payBadge.cls}`}>{payBadge.label}</span>
                </div>
              );
            })}
          </div>
        )}
        {amountDue > 0 && activePlans.length > 0 && (
          <div className="mt-2 px-3 py-2 bg-red-50/50 rounded-lg border border-red-100">
            <p className="text-xs text-red-700 font-medium">
              {isRTL ? `المبلغ المطلوب تحويله: ` : `Amount to transfer: `}
              <span className="font-bold" dir="ltr">{amountDue.toLocaleString()} {isRTL ? 'درهم' : 'AED'}</span>
            </p>
          </div>
        )}
      </div>

      <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
        <Button size="sm" onClick={handleSubmit} className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#0F172A]" data-testid="deposit-submit-btn">{t('save')}</Button>
        <Button size="sm" variant="outline" onClick={onClose} data-testid="deposit-cancel-btn">{t('cancel')}</Button>
      </div>
    </div>
  );
};
